<script setup lang="ts">
  import { computed, ref } from 'vue'
  import Typography from '@/components/common/Typography.vue'
  import RichTextRenderer from '@/components/common/RichTextRenderer.vue'
  import { usePronunciationCollection } from '@/composables/usePronunciationCollection'
  import type { PronunciationCollection } from '@/types/PronunciationCollection'
  import PronunciationBox from '@/components/PronunciationBox.vue'
  import PronounciationPagination from '@/components/PronunciationPagination.vue'

  const currentPage = ref(1)

  const pushPage = (pageNumber: number) => {
    currentPage.value = pageNumber
    setPage(pageNumber)
  }

  const props = defineProps<{
    element: PronunciationCollection,
  }>()

  const { currentPageIds, setPage, pageSize } = usePronunciationCollection(props.element)
  const itemCount = computed(() => props.element.pronunciationIds.length ?? 0)

</script>

<template>
  <div :class="['pronunciation-collection-wrapper', { 'blue-background': element.blueBackground }]">
    <div class="pronunciation-content">
      <Typography
        class="heading"
        component="h2"
        :font-weight="600"
      >
        {{ element?.heading }}
      </Typography>
      <RichTextRenderer
        v-if="element?.description"
        class="description"
        :rich-text="element?.description"
      />
      <PronounciationPagination
        v-if="element?.pagination"
        :current-page="currentPage"
        :item-count="itemCount"
        :page-size="pageSize"
        @push-page="pushPage"
      />
      <div class="pronunciation-collection">
        <template
          v-for="(pronunciationId) in currentPageIds"
          :key="pronunciationId"
        >
          <PronunciationBox :pronunciation-id="pronunciationId" />
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .pronunciation-collection-wrapper {
    width: 100%;
    padding: 75px 0;
    display: flex;
    justify-content: center;

    .heading {
      font-size: 2.5rem;
    }
  }

  .pronunciation-collection-wrapper.blue-background {
    background-color: var(--color-blue-50);

    .heading, .description {
      color: var(--color-lightblue-10);
    }

    :deep(.pronunciation-pagination) {
      .ks-button {
        color: var(--color-lightblue-10);
        border: 2px solid var(--color-lightblue-10);

        &.ks-button-primary {
          background: var(--color-lightblue-10);
          color: var(--color-blue-50);

          &:hover {
            background: var(--color-lightblue-20);
          }
        }

        &.ks-button-border:hover {
          background: var(--color-blue-60);
        }
      }
    }
  }

  .pronunciation-content {
    width: $vgs-max-width-content-large;
  }

  .pronunciation-collection {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 4px;
    min-height: 200px;

    @include vgs-media-tablet {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @include vgs-media-mobile {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
  </style>
